import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useIsLoggedIn, useUser} from "UserProvider";
import {useLoginModal} from "login/LoginModalProvider";
import {useResolvedStorefrontQuote} from "pricing/useQuotedPricing";
import {usePaidMoment} from "upsell/usePaidMoment";
import {readReferralCodeFromQuery, storedReferralCode} from "./referralAttribution";
import {
	claimableReferralIntent, forgetReferralClaimIntent, rememberReferralClaimIntent,
	storedReferralClaimIntent, type ReferralClaimIntent,
} from "./referralClaimIntent";
import {
	anonymousReferralOfferModel, referralLandingStage,
	type ReferralLandingStage,
} from "./referralLandingOffer";
import {
	referralOfferModel,
	type ReferralOfferChoice, type ReferralOfferModel, type ReferralOfferVariant,
} from "./referralOffer";
import {dismissReferralLink, hasDismissedReferralLink} from "./referralOfferDismissal";
import {useReferralDialogSlot} from "./useReferralDialogSlot";
import type {ReferralClaim, ReferralClaimStatus} from "./useReferralClaim";
import type {ReferralAttributionOutcome} from "./useReferralAttribution";

/**
 * The landing picker's state, from the arrival on a share link to the checkout after signup
 * (kitecorp/transferify-ui#2020).
 *
 * A thin React adapter by mandate: every rule it applies lives in the React-free
 * `./referralLandingOffer`, `./referralClaimIntent` and `./referralOfferDismissal`. This holds the
 * browser facts those rules are asked about (the code in the URL, the dismissal, the remembered
 * choice), the storefront the amounts come from, and the one flag that says a sign-in is on screen.
 *
 * <b>Mounted on the app SHELL</b>, for the reason `useReferralAttribution` gives for the same choice:
 * a share link can point at any public route, and the sign-in it raises can complete on any route too,
 * so a hook that only ran on `/` would lose both halves of most journeys.
 */
export interface ReferralLandingOfferState {
	/** What the landing owes this reader, before the sign-in modal is taken into account. */
	stage: ReferralLandingStage;
	/**
	 * The picker is on screen.
	 *
	 * False while the sign-in it raised is, and false while another mount is showing the same offer
	 * (`./referralDialogSlot`), so the reader only ever has one of them in front of them.
	 */
	pickerOpen: boolean;
	/** The plain "this gift could not be applied" notice is on screen. */
	noticeOpen: boolean;
	/** The picker's figures for the current stage, or null when there is nothing to state. */
	model: ReferralOfferModel | null;
	/** The row the picker opens on, or null to open on the model's own first row. */
	initialVariant: ReferralOfferVariant | null;
	/** No account yet, which is what decides whether pressing Continue signs up or pays. */
	anonymous: boolean;
	/** The anonymous press: remember the tier for this browser, then raise the sign-in. */
	choose: (choice: ReferralOfferChoice) => Promise<void>;
	/** The ✕, the scrim, Escape and "Stay on Free". */
	close: () => void;
	/** The embedded sheet completed a payment, which is not a dismissal. */
	closeAfterPaid: () => void;
	closeNotice: () => void;
}

export function useReferralLandingOffer(
	attribution: ReferralAttributionOutcome,
): ReferralLandingOfferState {
	const isLoggedIn = useIsLoggedIn();
	const [user] = useUser();
	const {open: openLogin} = useLoginModal();

	/*
	 * THE BROWSER FACTS, read once after mount rather than during render.
	 *
	 * Both of them (the query string and a stored dismissal) are invisible to the SSG prerender, so a
	 * render that consulted them would differ from the pre-rendered HTML and React would discard the
	 * subtree (ui#74/#418). This is the same ordering `useMountedHostname` relies on, and the same
	 * mount-time read `useReferralAttribution` captures the code with.
	 */
	const [arrivingCode, setArrivingCode] = useState<string | null>(null);
	const [dismissed, setDismissed] = useState(false);
	const [intent, setIntent] = useState<ReferralClaimIntent | null>(null);
	const [awaitingSignIn, setAwaitingSignIn] = useState(false);
	const [noticeOpen, setNoticeOpen] = useState(false);
	useEffect(() => {
		const code = readReferralCodeFromQuery(window.location.search);
		setArrivingCode(code);
		setDismissed(hasDismissedReferralLink(code));
		setIntent(storedReferralClaimIntent(Date.now()));
	}, []);

	/*
	 * The code this visit is ACTING ON, and the remembered tier that belongs to it.
	 *
	 * The attribution's code once it has one, because that is the gift being claimed; the arriving
	 * link's before that. A remembered choice whose code is neither is not this visit's (see
	 * `claimableReferralIntent`), and is what would otherwise keep the storefront read below armed
	 * for a month after the offer it was made for lapsed.
	 */
	const activeCode = attribution.code ?? arrivingCode;
	const intentVariant = claimableReferralIntent(intent, activeCode);

	// Whether anybody is looking, which is what lets `useResolvedStorefrontQuote` ask the api at all:
	// an ungated read would put a `/plan` request on every visit to every public route.
	const shown = arrivingCode !== null || intentVariant !== null;
	const {storefront, settled} = useResolvedStorefrontQuote(shown);

	/*
	 * The picker for a reader with no account, and the one for an account that has been given the gift.
	 * Both are gated on a SETTLED storefront: a reader in Great Britain is quoted euros for a moment
	 * before the api's pound answer arrives, and an offer that opened on the first of those would state
	 * an amount this storefront cannot charge.
	 */
	const anonymousModel = useMemo(
		() => settled ? anonymousReferralOfferModel(storefront) : null,
		[settled, storefront]);
	const accountModel = useMemo(
		() => settled ? referralOfferModel(user.referralOffer, storefront) : null,
		[settled, user.referralOffer, storefront]);

	const stage = referralLandingStage({
		arrivingCode, signedIn: isLoggedIn, dismissed, intent: intentVariant,
		attribution: attribution.status, anonymousModel, accountModel,
	});

	/*
	 * A REFUSED attribution must not survive a reload, so the stored choice is dropped the moment the
	 * server answers. The in-memory `intent` deliberately outlives it: it is what keeps the notice on
	 * screen for the reader who was promised the gift, and dropping both at once would clear the
	 * refusal off the page before anyone read it.
	 */
	const refusalHandled = useRef(false);
	useEffect(() => {
		if (stage !== "refused" || refusalHandled.current) {
			return;
		}
		refusalHandled.current = true;
		forgetReferralClaimIntent();
		setNoticeOpen(true);
	}, [stage]);

	const model = stage === "claiming" ? accountModel : anonymousModel;

	/*
	 * ONE REFERRAL DIALOG AT A TIME (kitecorp/transferify-ui#2047).
	 *
	 * What the stage says this mount owes the reader is not yet whether it may show it: the page under
	 * the shell mounts the SAME picker for the SAME claim — `/pricing` raises it from a ladder gift link
	 * (ui#2033) and from a swapped card CTA (ui#1993) — and on one load both opened, so the reader had
	 * to close the same offer twice. `./referralDialogSlot` owns the rule; this mount asks it like any
	 * other picker and takes the answer.
	 */
	const due = stage === "claiming" || (stage === "offering" && !awaitingSignIn);
	const dialog = useReferralDialogSlot("referral-landing", due);
	const pickerOpen = dialog.open;

	/*
	 * YIELDING: the claim is spent when another picker is already making it.
	 *
	 * A refusal is not a wait. The dialog the reader is looking at states this same gift at this same
	 * price and claims it through the same checkout, so the stored intent has done its job and is
	 * dropped — leaving it would raise this mount's copy on the next route, which is the interruption
	 * this rule exists to remove rather than reschedule.
	 *
	 * <b>The claim is dropped, not the gift</b>, exactly as {@link close} leaves it: the account card,
	 * the create panel's strip and the pricing cards still carry it. Nothing is reported dismissed
	 * either — the reader dismissed nothing, and this mount showed nothing to dismiss.
	 *
	 * Only a CLAIM can be refused in practice: the anonymous offer belongs to a reader with no account,
	 * and every page-side picker is gated on a gift published on one.
	 */
	useEffect(() => {
		if (!dialog.refused) {
			return;
		}
		forgetReferralClaimIntent();
		setIntent(null);
	}, [dialog.refused]);

	// The row the picker will OPEN on, which is the row whose price the reader is about to be shown.
	const openingChoice = model === null
		? null
		: model.choices.find((choice) => choice.variant === intentVariant) ?? model.choices[0];

	/*
	 * The funnel's shown and price_seen steps (ui#1965), for the anonymous arrival as well as the claim
	 * that follows it (kitecorp/transferify-ui#2034).
	 *
	 * No sign-in in `visible`, deliberately. The picker's whole point is that it opens on the landing a
	 * share link points at, so gating the report on an account measured the remainder of a surface most
	 * readers meet without one. The subscriber routes it: with a session it goes to the authenticated
	 * beacon, and without one to `POST /paywall/events`, which admits this surface's appearance and its
	 * walk-away. Nothing here knows either transport exists.
	 */
	const paidMoment = usePaidMoment("REFERRAL_TRIAL", {
		visible: pickerOpen && model !== null,
		// The OPENING row's intro, not the model's first: a resumed claim opens on the tier the reader
		// chose before signing up, and recording the cheapest row instead would file a Premium showing
		// under the Plus entry price in the very report these steps exist to produce.
		price: model === null || openingChoice === undefined || openingChoice === null
			? null
			: {currency: model.currency, amountMinor: openingChoice.introMinor},
	});

	const choose = useCallback(async (choice: ReferralOfferChoice) => {
		if (arrivingCode === null) {
			return;
		}
		// The code this browser will SUBMIT, which first touch may have decided before this link
		// arrived. Storing the arriving one instead would record a choice against a gift the
		// attribution is never going to ask for.
		const now = Date.now();
		const claimIntent = {variant: choice.variant, code: storedReferralCode(now) ?? arrivingCode};
		rememberReferralClaimIntent(claimIntent, now);
		setIntent(claimIntent);
		// The picker steps aside while the sign-in is up, so the landing never stacks two modals; a
		// reader who backs out of the sign-in gets it straight back with their choice still selected.
		setAwaitingSignIn(true);
		const signedIn = await openLogin();
		if (!signedIn) {
			setAwaitingSignIn(false);
		}
	}, [arrivingCode, openLogin]);

	const close = useCallback(() => {
		// The walk-away, on either picker (kitecorp/transferify-ui#2034). The anonymous close is the one
		// the owner asked to count: an arrival that closes the gift is the outcome `dismissed / shown` on
		// this surface is a rate of, and it happens before the friend has an account.
		//
		// Guarded on the same expression the showing is, so the two can never disagree: a mount refused
		// the dialog slot (ui#2047) reports neither, and this mount cannot report a close of something it
		// never put on screen.
		if (pickerOpen && model !== null) {
			paidMoment.reportDismissed();
		}
		if (stage === "claiming") {
			// The claim is dropped, not the gift: the account card and the create panel's strip still
			// carry it, which is the surface the reader is left with.
			forgetReferralClaimIntent();
			setIntent(null);
			return;
		}
		dismissReferralLink(arrivingCode);
		forgetReferralClaimIntent();
		setDismissed(true);
		setIntent(null);
	}, [stage, pickerOpen, model, arrivingCode, paidMoment]);

	const closeAfterPaid = useCallback(() => {
		forgetReferralClaimIntent();
		setIntent(null);
	}, []);

	const closeNotice = useCallback(() => {
		setNoticeOpen(false);
		setIntent(null);
	}, []);

	return {
		stage,
		pickerOpen,
		noticeOpen,
		model,
		initialVariant: intentVariant,
		anonymous: !isLoggedIn,
		choose,
		close,
		closeAfterPaid,
		closeNotice,
	};
}

/**
 * The picker's CTA for a reader with no account: it remembers the tier and raises the sign-in, and
 * mints no Stripe object at all.
 *
 * <b>It satisfies {@link ReferralClaim} so the dialog needs no anonymous branch.</b> One picker, two
 * adapters: this one and the real `useReferralClaim`. Teaching the dialog to tell them apart would put
 * a second money path inside a component whose whole job is stating amounts.
 *
 * <b>The acknowledgement is still required.</b> Pressing Continue here commits the reader to an
 * auto-renewing intro period that is charged as soon as their account exists, so the renewal terms and
 * the tick belong on the press that makes the commitment. The api asks for it again at the checkout
 * that follows, which is where it is enforced; an acknowledgement is never carried across an account
 * boundary.
 */
export function useAnonymousReferralClaim(
	choose: (choice: ReferralOfferChoice) => Promise<void>,
): ReferralClaim {
	const [acknowledged, setAcknowledged] = useState(false);
	const [status, setStatus] = useState<ReferralClaimStatus>("idle");

	const claim = useCallback(async (choice: ReferralOfferChoice) => {
		if (!acknowledged) {
			return;
		}
		setStatus("starting");
		try {
			await choose(choice);
		} finally {
			setStatus("idle");
		}
	}, [acknowledged, choose]);

	return useMemo(() => ({
		// No sheet can be mounted without an account, so there is never a secret and never a completion.
		clientSecret: null,
		trialHold: null,
		collectsShippingAddress: false,
		status,
		acknowledged,
		setAcknowledged,
		claim,
		dismiss: () => {},
		completed: () => {},
	}), [status, acknowledged, claim]);
}
